/**
 * Codex Auth Terminal
 *
 * Embedded terminal for Codex device authentication.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { scheduleAuthTerminalLifecycle } from './authTerminalLifecycle';
import '@xterm/xterm/css/xterm.css';

export type CodexTerminalStage =
  | 'starting'
  | 'device-code'
  | 'waiting'
  | 'success'
  | 'error';

interface CodexAuthTerminalProps {
  onSuccess: () => void;
  onCancel: () => void;
  onStageChange?: (stage: CodexTerminalStage) => void;
}

export function CodexAuthTerminal({
  onSuccess,
  onCancel,
  onStageChange,
}: CodexAuthTerminalProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstance = useRef<Terminal | null>(null);
  const fitAddon = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const terminalCleanupRef = useRef<(() => void) | null>(null);
  const onSuccessRef = useRef(onSuccess);
  const onStageChangeRef = useRef(onStageChange);
  const currentStageRef = useRef<CodexTerminalStage>('starting');
  const [authComplete, setAuthComplete] = useState(false);
  const [isTerminalFocused, setIsTerminalFocused] = useState(false);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onStageChangeRef.current = onStageChange;
  }, [onSuccess, onStageChange]);

  const setStage = useCallback((stage: CodexTerminalStage) => {
    if (currentStageRef.current === stage) return;
    currentStageRef.current = stage;
    onStageChangeRef.current?.(stage);
  }, []);

  const completeAuth = useCallback(() => {
    if (authComplete) return;
    setStage('success');
    setAuthComplete(true);
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    onSuccessRef.current();
  }, [authComplete, setStage]);

  const checkAuthStatus = useCallback(async () => {
    try {
      const status = await api.codex.getStatus();
      if (status.authenticated) {
        completeAuth();
        return true;
      }
    } catch (error) {
      console.error('[CodexAuthTerminal] Status check failed:', error);
    }
    return false;
  }, [completeAuth]);

  const processTerminalOutput = useCallback((text: string) => {
    const normalized = text.toLowerCase();

    if (
      normalized.includes('auth.openai.com/codex/device') ||
      normalized.includes('device') ||
      normalized.includes('one-time code') ||
      normalized.includes('verification')
    ) {
      setStage('device-code');
    }

    if (
      normalized.includes('waiting') ||
      normalized.includes('polling') ||
      normalized.includes('complete authorization')
    ) {
      setStage('waiting');
    }

    if (
      normalized.includes('logged in') ||
      normalized.includes('success') ||
      normalized.includes('authenticated')
    ) {
      void checkAuthStatus();
    }

    if (
      normalized.includes('error') ||
      normalized.includes('failed') ||
      normalized.includes('expired')
    ) {
      setStage('error');
    }
  }, [checkAuthStatus, setStage]);

  useEffect(() => {
    const terminalContainer = terminalRef.current;
    if (!terminalContainer || terminalInstance.current) return;

    const cleanupTerminal = scheduleAuthTerminalLifecycle(() => {
      const terminal = new Terminal({
        cursorBlink: true,
        fontSize: 13,
        fontFamily: 'JetBrains Mono, Menlo, Monaco, monospace',
        theme: {
          background: '#1a1b26',
          foreground: '#a9b1d6',
          cursor: '#c0caf5',
          cursorAccent: '#1a1b26',
          selectionBackground: '#33467c',
        },
        rows: 15,
        cols: 120,
      });

      const fit = new FitAddon();
      const webLinks = new WebLinksAddon();

      terminal.loadAddon(fit);
      terminal.loadAddon(webLinks);
      terminal.open(terminalContainer);
      fit.fit();

      terminalInstance.current = terminal;
      fitAddon.current = fit;

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${protocol}//${window.location.host}/ws/pty`);
      wsRef.current = ws;

      ws.onopen = () => {
        terminal.focus();
        setIsTerminalFocused(true);
        fit.fit();
        ws.send(JSON.stringify({
          type: 'resize',
          cols: terminal.cols,
          rows: terminal.rows,
        }));
      };

      const textarea = terminalContainer.querySelector('textarea');
      const handleFocus = () => setIsTerminalFocused(true);
      const handleBlur = () => setIsTerminalFocused(false);

      if (textarea) {
        textarea.addEventListener('focus', handleFocus);
        textarea.addEventListener('blur', handleBlur);
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
            return;
          }

          if (msg.type === 'ready') {
            ws.send(JSON.stringify({ type: 'data', data: 'codex login --device-auth\r' }));
            return;
          }

          if (msg.type === 'error') {
            setStage('error');
            return;
          }

          if (msg.type === 'data' && msg.data) {
            terminal.write(msg.data);
            processTerminalOutput(msg.data);
          }
        } catch {
          terminal.write(event.data);
          processTerminalOutput(event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('[CodexAuthTerminal] WebSocket error:', error);
        terminal.writeln('\x1b[31mConnection error. Please try again.\x1b[0m');
        setStage('error');
      };

      ws.onclose = () => {
        console.log('[CodexAuthTerminal] WebSocket closed');
      };

      terminal.onData((data) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'data', data }));
        }
      });

      const handleResize = () => {
        fit.fit();
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'resize',
            cols: terminal.cols,
            rows: terminal.rows,
          }));
        }
      };

      window.addEventListener('resize', handleResize);

      return {
        deactivate: () => {
          window.removeEventListener('resize', handleResize);
          if (textarea) {
            textarea.removeEventListener('focus', handleFocus);
            textarea.removeEventListener('blur', handleBlur);
          }
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;
          if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
            ws.close();
          }
          if (wsRef.current === ws) {
            wsRef.current = null;
          }
        },
        dispose: () => {
          terminal.dispose();
          if (terminalInstance.current === terminal) {
            terminalInstance.current = null;
          }
          if (fitAddon.current === fit) {
            fitAddon.current = null;
          }
        },
      };
    });

    terminalCleanupRef.current = cleanupTerminal;
    return () => {
      if (terminalCleanupRef.current === cleanupTerminal) {
        terminalCleanupRef.current = null;
      }
      cleanupTerminal();
    };
  }, [processTerminalOutput, setStage]);

  useEffect(() => {
    if (authComplete) {
      terminalCleanupRef.current?.();
      terminalCleanupRef.current = null;
    }
  }, [authComplete]);

  useEffect(() => {
    if (authComplete) return;

    const interval = window.setInterval(() => {
      void checkAuthStatus();
    }, 3000);

    return () => window.clearInterval(interval);
  }, [authComplete, checkAuthStatus]);

  const handleTerminalClick = () => {
    terminalInstance.current?.focus();
  };

  if (authComplete) {
    return (
      <div className="text-green-400 text-sm font-medium py-4">
        Authentication successful
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className={`text-xs font-medium transition-colors ${
        isTerminalFocused ? 'text-green-400' : 'text-muted-foreground'
      }`}>
        {isTerminalFocused ? 'Terminal active' : 'Click terminal to activate'}
      </div>

      <div
        className={`relative overflow-hidden rounded-md border-2 w-full transition-colors ${
          isTerminalFocused
            ? 'border-green-500 ring-2 ring-green-500/20'
            : 'border-border hover:border-muted-foreground'
        }`}
        style={{ height: '400px' }}
        onClick={handleTerminalClick}
      >
        <div
          ref={terminalRef}
          className="absolute inset-0 cursor-text"
        />
      </div>

      <Button
        variant="destructive"
        size="sm"
        onClick={onCancel}
        className="w-fit"
      >
        Cancel
      </Button>
    </div>
  );
}

export default CodexAuthTerminal;
